"use client";

import * as React from "react";
import {
  DirectorySyncError,
  DirectorySyncLoading,
  DirectorySync as DirectorySyncPresentational,
} from "./lib/directory-sync.js";
import type {
  DirectorySyncErrorProps,
  DirectorySyncLoadingProps,
} from "./lib/directory-sync.js";
import { ApiProvider, AuthToken, useApiReady } from "./api/api-provider.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import { ErrorBoundary } from "./lib/error-boundary.js";
import type { WidgetRootDomProps } from "./lib/utils.js";
import { DirectoryState, useListDirectories } from "./api/endpoint.js";
import { usePermissions } from "./lib/use-permissions.js";
interface DirectorySyncProps extends WidgetRootDomProps {
  authToken: AuthToken;
}

const DirectorySync: React.FC<DirectorySyncProps> = ({
  authToken,
  ...domProps
}) => {
  const baseUrl = useWorkOsApiUrl();

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <DirectorySyncError {...domProps} error={error} />
      )}
    >
      <ApiProvider
        widgetType="directory-sync"
        authToken={authToken}
        baseUrl={baseUrl}
      >
        <DirectorySyncImpl {...domProps} />
      </ApiProvider>
    </ErrorBoundary>
  );
};

const DirectorySyncImpl = (props: WidgetRootDomProps) => {
  const isApiReady = useApiReady();
  const { data: directories, isLoading, isError, error } = useListDirectories();
  const permissionsQuery = usePermissions("widgets:dsync:manage");

  if (!isApiReady || isLoading || permissionsQuery.isLoading) {
    return <DirectorySyncLoading {...props} />;
  }

  if (permissionsQuery.isError || isError) {
    return (
      <DirectorySyncError error={permissionsQuery.error || error} {...props} />
    );
  }

  if (!directories?.data?.length) {
    return (
      <DirectorySyncPresentational
        {...props}
        state={DirectoryState.unlinked}
        link={directories?.link}
      />
    );
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <DirectorySyncError error={error} {...props} />
      )}
    >
      {directories.data.map((directory) => {
        const { id, state, type, metadata, lastSyncRunAt } = directory;
        const commonProps = {
          ...props,
          type,
          metadata,
          lastSyncAt: lastSyncRunAt ?? null,
          link: directories.link,
        };

        switch (state) {
          case DirectoryState.invalid_credentials:
            return (
              <DirectorySyncPresentational
                key={id}
                {...commonProps}
                state={state}
                errorMessage="Your directory credentials are invalid or have expired. Please re-authenticate to resume syncing."
              />
            );
          case DirectoryState.linked:
            return (
              <DirectorySyncPresentational
                key={id}
                {...commonProps}
                state={state}
              />
            );
          case DirectoryState.validating:
            return (
              <DirectorySyncPresentational
                key={id}
                {...commonProps}
                state={state}
              />
            );
          case DirectoryState.deleting:
            return (
              <DirectorySyncPresentational
                key={id}
                {...commonProps}
                state={state}
              />
            );
          case DirectoryState.unlinked:
            return (
              <DirectorySyncPresentational
                key={id}
                {...commonProps}
                state={state}
              />
            );
          case DirectoryState.requires_type:
            return (
              <DirectorySyncPresentational
                key={id}
                {...commonProps}
                state={state}
                errorMessage="Your directory requires additional configuration. Please complete setup to begin syncing."
              />
            );
        }
      })}
    </ErrorBoundary>
  );
};

export type {
  DirectorySyncProps,
  DirectorySyncLoadingProps,
  DirectorySyncErrorProps,
};
export { DirectorySync, DirectorySyncLoading, DirectorySyncError };
